// react and css essentials
import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { truthSeeker, chaos } from "./chapters/chapters";

// import { chapters } from "./chapters/chapters";
import styles from "./factCheckerPage.module.css";
import theme from "../../../theme/theme.module.css";


// components
import Loading from "../../../components/loading/loading";
import Headline from "../../../components/headline/headline";
import Summary from "../../../components/summary/summary";
import AnimationChapterSbgame from "../../../components/animationChapterSbgame/animationChapterSbgame";
import { AnswerCommon, AnswerLikert, AnswerDropdown, AnswerCheckbox,
 AnswerProfile, AnswerCarrousel, AnswerMatrix, AnswerUniqueHandle} from '../../../components/answerTypes';
import PostMessage from "../../../components/postMessage/postMessage";
import SectionTitle from "../../../components/sectionTitle/sectionTitle";
import ConfirmationTeenGame from "../../../components/confirmationTeenGame/confirmationTeenGame";
import SettingsTeenGame from "../../../components/confirmationTeenGame/settingsTeenGame";

// functions
import { getRandomName } from "../../../functions/getRandomUserName";
import { useLanguage } from '../../../functions/setGetLanguage';
import { renderWithLineBreaksAndBold } from '../../../functions/renderWithLineBreaksAndBold';
import { useUserData } from '../../../functions/updateUserData';
import { useSettings } from '../../../functions/updateSettings';
import { useTranslation } from "react-i18next";


import { setUserInfo, saveSurveyAnswers, saveTraceStep } from "../../../api/api";

// assets
import ImgBuilder from "../../../assets/ImgBuilder";

const FactCheckerPage = () => {
  const navigate = useNavigate();
  const { type } = useParams(); 
  const { t } = useTranslation();
  const MATRIX_SIZE = 3 // emotional - conspiracy - normal
  
  // Compute chapters immediately
  const chapters = useMemo(() => {
    if (type === "truth-seeker") return truthSeeker;
    if (type === "chaos") return chaos;
    return null; // invalid type
  }, [type]);

  // Navigate away if type is invalid
  useEffect(() => {
    if (chapters === null) {
      navigate("/");
    }
  }, [chapters, navigate]);

  // since it is a survey, to prevent unneccesary reloading errors.
  const useSessionStorageState = (key, defaultValue) => {
    // sessionStorage saves values until tab is closed
    // initialize the values as state
    const [state, setState] = useState(() => {
      const saved = sessionStorage.getItem(key);
      return saved ? JSON.parse(saved) : defaultValue;
    });
    // update the values in sessionStorage
    useEffect(() => {
      sessionStorage.setItem(key, JSON.stringify(state));
    }, [key, state]);
    return [state, setState];
  }

  // const [chapterIndex, setChapterIndex] = useState(0);
  const [chapterIndex, setChapterIndex] = useSessionStorageState("chapterIndex",0);
  const [themeName, setThemeName] = useSessionStorageState("themeName","default");
  const [history, setHistory] = useState([]);
  const [currentId, setCurrentId] = useState("start");
  const { userData, setUserData } = useUserData();
  const { settings, setSettings } = useSettings();
  const [isWaiting, setIsWaiting] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isConfirmation, setIsConfirmation] = useState(false);
  const [isSettings, setIsSettings] = useState(false);
  const [carrousel, setCarrousel] = useState(0);
  const [apiTrigger, setApiTrigger] = useState(null);
  // const stepRef = useRef(0);
  const chatEndRef = useRef(null);

  // language
  const { language, setLanguage } = useLanguage();

  // dialogue
  const currentChapter = chapters[chapterIndex];
  const dialogueData = currentChapter.data;
   

  // Initial message setup - after new headline appears

  useEffect(() => {
    document.documentElement.style.setProperty("--text-size", settings.text_size.toString());
  }, [settings.text_size]);

  useEffect(() => {
    console.log("isSettings changed:", isSettings);
  }, [isSettings]);

  useEffect(() => {
    if (isAnimating) return; // do not run while the animation is in play
    const firstNode = dialogueData.find((item) => item.id === "start");
    if (!firstNode) return;

    const titleMessage = { speaker: "section-title", label: currentChapter[`label${language}`] };
    const firstMessage = {
      ...firstNode,
      text: applyVariables(firstNode[`text${language}`]),
    };

    setHistory([titleMessage, firstMessage]);

    if (!firstNode.choices && firstNode.next) {
      setIsWaiting(true);
      setCurrentId(firstNode.next);
    } else {
      setCurrentId(firstNode.id);
      setIsWaiting(false);
    }
  }, [chapterIndex, isAnimating]);

  // Auto-progress scripted messages - next nodes after the headline
  useEffect(() => {
    if (currentId === "start") {
      return;
    }
    const node = dialogueData.find((item) => item.id === currentId);

    const delay = (Math.random() * 1800 + 1900) / settings.text_speed;
    setIsWaiting(true);

    if (!node) {
      const delay_nonodes = 0.3*delay
      setTimeout(() => {
        setIsWaiting(false); // Only stop waiting after the delay
      }, delay_nonodes);
      return 
    }
    

    const timeout = setTimeout(() => {
      let newMessage
      if (node[`text${language}`]){
        const textWithVars = applyVariables(node[`text${language}`]);
        newMessage = { ...node, text: textWithVars };
      } else{
        newMessage = { ...node, };
      }

      // Only assign userName for regular "post"
      if (node.speaker === "post") {
        newMessage.userName = `@${getRandomName()}`;
      }

      setHistory((prev) => [...prev, newMessage]);
      setIsWaiting(false);
      
      if (node.choice_paths) {
        for (const { check_variable, next } of node.choice_paths) {
          if (Object.entries(check_variable).every(([key, val]) => userData[key] === val)) {
            node.next = next;
            break;
          }
        }
      }

      if (!node.choices && node.next) {
        setIsWaiting(true);
        setCurrentId(node.next);
      }
    }, delay);

    return () => clearTimeout(timeout);
  }, [currentId]);

  // Auto scroll to bottom (after every message)
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history]);
  
  // replace key with a text (name for example)
  const applyVariables = (text) =>
    text.replace(/\$(\w+)/g, (_, key) => userData[key] || "");

  const handleApi = async () => {
    const handlers = {
      demographic: async (userTraceID) => {
        try {
          await setUserInfo(userData.tokens.access_token, userData.demographic);
        } catch (err) {
          console.error("Error: ",err);
        }
      },

      survey_teen: async (userTraceID) => {
        try {
          // as it recieves only arrays -> I send only arrays, so I make all ints a 1-long array
          // TODO: if /survey/pos-op/save accepts int, delete this part as it is redundancy
          const formattedSurveyData = Object.fromEntries(
            Object.entries(userData.survey_teen).map(([key, value]) => [
              key,
              Array.isArray(value) ? value.map(Number) : [Number(value)]
            ])
          );
          await saveSurveyAnswers(userData.tokens.access_token, userTraceID, "survey_media", formattedSurveyData);
        } catch (err) {
          console.error(err);
        }
      },

      headlines: async (userTraceID) => {
        try {
          // const step_num = stepRef.current;
          const [trace_step_id,answer_value] = Object.entries(apiTrigger[1])[0] 
          const traceStep = {
            language: language.substring(1), // cause it is saved as "_en" or "_de", or "_es"
            trace_id: +userTraceID,
            step_num: trace_step_id, // can be also 'step_num'
            answer: answer_value,
            mode: "original"
          };
          // stepRef.current += 1;
          await saveTraceStep(userData.tokens.access_token, traceStep);
        } catch (err) {
          console.error(err);
        }
      },
    };
    const userTraceID =  userData.userTraceID ?? -2
    await handlers[apiTrigger[0]](userTraceID); // Await the actual handler
  };

  
  useEffect(() => {
    if (!apiTrigger) return;
    const runApiTrigger = async () => {
      try {
        await handleApi();
      } finally {
        setApiTrigger(null);
      }
    };
    runApiTrigger();
  }, [apiTrigger, userData]);


  // called every time button is pressed
  const handleChoice = (choice) => {
    // // means instead of a dict I recieved an array of dicts (choices_checkbox)
    // // which has multiple possible answers.
    if (Array.isArray(choice)) {
      for (const item of choice) {
        handleChoice(item);
      }
      return;
    }
    // if pathes or if urls
    if (choice.choice_paths) {
      for (const { check_variable, next } of choice.choice_paths) {
        if (Object.entries(check_variable).every(([key, val]) => userData[key] === val)) {
          choice.next = next;
          break;
        }
      }
    } else if (choice.choice_urls) {
      // case if the button should have an url
      // end of the game
      resetToDefault()
      navigate(choice.choice_urls);
    }

    const speaker = choice.speaker || "player";
    const newMessage = {
      text: choice[`text${language}`],
      speaker,
    };

    // Assign @name for player_post only once
    if (speaker === "player_post") {
      newMessage.userName = `@${userData.selected_profile || "Expert"}`;
    }
    
    // stores variables
    setHistory((prev) => [...prev, newMessage]);
    if (choice.store_variable) {
      if (choice.stack_storing) {
        setUserData((prev) => {
          const key = choice.stack_storing;
          const valueToStore = choice.store_variable;
          const existingStack = prev[key] || {};
          return { ...prev, [key]:  { ...existingStack, ...valueToStore } };
        });
      } else{
        setUserData(p => ({ ...p, ...choice.store_variable }));
      }
    }

    // erases variables 
    if (choice.erase_variable) {
      setUserData(prev => {
        const { [choice.erase_variable]: _, ...rest } = prev;
        return rest;
      });
    }

    // triggers API iff data consent not required or iff it is given
    if (
    !choice.check_variable ||
    Object.entries(choice.check_variable).every(
      ([key, value]) => key in userData && userData[key] === value
    )) {
      if (choice.triggerApi) {
        setApiTrigger([choice.triggerApi,(choice.store_variable ?? null)]);
      };
    }
    
    // modifies variables (for statistics) - currently only addition, can be modified further
    if (choice.update_variable) {
      setUserData(prev => {
        const updated = { ...prev };

        for (const [key, value] of Object.entries(choice.update_variable)) {
          const current = updated[key] ?? 0;
          updated[key] = current + value;
        }

        return updated;
      });
    }

    // handle confusion matrix
    if (choice.matrix_update) {
      setUserData(prev => {
        // initialize if missing
        const matrix = prev.confusion_matrix ?? Array.from({ length: MATRIX_SIZE }, () =>
          Array(MATRIX_SIZE).fill(0)
        );

        const [row, col] = choice.matrix_update;

        const newMatrix = matrix.map(r => [...r]);
        newMatrix[row][col] += 1;

        return { ...prev, confusion_matrix: newMatrix };
      });
    }

    setIsWaiting(true);
    setCurrentId(choice.next);
  };

  const currentNode = dialogueData.find((item) => item.id === currentId);

  // handles buttons that go to the next chapter
  const handleChapterComplete = () => {
    const nextIndex = chapterIndex + 1;
    const nextChapter = chapters[nextIndex];

    if (!nextChapter) {
      // should not be a case
      navigate("/")
      return;
    } else{
      if (nextChapter.introductionScreen) {
        const introductionScreen = {"introductionScreen": nextChapter.introductionScreen}
        setUserData((prev) => ({ ...prev, ...introductionScreen }));
        setIsAnimating(true);
        setChapterIndex(nextIndex);
        setHistory([]);
        setThemeName(nextChapter.style ? nextChapter.style : "default");
      } else{
        setChapterIndex(nextIndex);
        setHistory([]);
        setThemeName(nextChapter.style ? nextChapter.style : "default");
      }
    }
  }

  // confirmations (currently only one):
  const toggleConfirmationComponent = (action) => {
    // handle reset and navigate away (will be a separate function ideally)
    if (action){
      resetToDefault()
      navigate("/");
    }
    // just open/close it
    else{
      setIsConfirmation(!isConfirmation)
    }
  }

  const resetToDefault = () => {
    sessionStorage.clear();
    localStorage.clear();
    flushSync(() => {
      setChapterIndex(0);
      setHistory([]);
      setCurrentId("start");
      setUserData({});
      setLanguage("_en");
      setSettings({"text_size": 1, "text_speed": 1});
    });
    return
  }

  const settingsFunction = () => {
    console.log("SFSDSDSDSDSD")
    setIsSettings(prev => !prev);
  }

  // rendering history (useState for the list "history")
  // speaker is type of a message
  const renderHistory = {
    "section-title": (msg, idx) => (
      <SectionTitle key={idx} msg={msg} abortFunction={toggleConfirmationComponent} settingsFunction={settingsFunction}/>
    ),
    "post": (msg, idx) => (
      <PostMessage key={idx} msg={msg} idx={idx} variables={userData} />
    ),
    "player_post": (msg, idx) => (
      <PostMessage key={idx} msg={msg} idx={idx} variables={userData} />
    ),
    "player": (msg, idx) => (
      <div key={idx} className={`${styles.messageRow} ${styles.right}`}>
        <div className={styles.message}>{msg.text}</div>
      </div>
    ),
    "editor": (msg, idx) => (
      <div key={idx} className={`${styles.messageRow} ${styles.left}`}>
        <div className={styles.message}>
          {renderWithLineBreaksAndBold(msg.text)}
        </div>
      </div>
    ),
    "sticker": (msg, idx) => (
      <div key={idx} className={`${styles.messageRow} ${styles.left}`}>
        <img alt="sticker" className={styles.sticker} src={ImgBuilder[msg.asset]}/>
      </div>
    ),
    "headline": (msg, idx) => (
      <Headline key={idx} msg={msg} idx={idx} variables={userData} />
    ),
    "summary": (msg, idx) => (
      <Summary key={idx} msg={msg} idx={idx} variables={userData} />
    ),
    default: (msg, idx) => (
      <p key={idx} ><b>Error:</b> The type of message given is non-existent, the message is: {msg}</p>
    ),
  };

  // rendering history (useState for the list "history")
  // choices is type of a response
  const renderAnswers = {
    "choices": (currentNode) => (
      <AnswerCommon 
        choices={currentNode.choices}
        FunctionOnClick={handleChoice}
      />
    ),
    "choices_profile": (currentNode) => (
      <AnswerProfile 
        choices_cr={currentNode.choices_profile}
        choices={currentNode.choices}
        FunctionOnClick={handleChoice}
        carrousel={carrousel}
        setCarrousel={setCarrousel}
        variables={userData}
        minSwipeDistance={50}
      />
    ),
    "choices_carrousel": (currentNode) => (
      <AnswerCarrousel 
        choices_cr={currentNode.choices_carrousel}
        choices={currentNode.choices}
        FunctionOnClick={handleChoice}
        carrousel={carrousel}
        setCarrousel={setCarrousel}
        variables={userData}
        minSwipeDistance={60}
      />
    ),
    "choices_matrix": (currentNode) => (
      <AnswerMatrix 
        choices={currentNode.choices_matrix}
        FunctionOnClick={handleChoice}
      />
    ),
    "choices_checkbox": (currentNode) => (
      <AnswerCheckbox 
        choices={currentNode.choices_checkbox}
        FunctionOnClick={handleChoice}
        submit={currentNode.submit}
      />
    ),
    "choices_dropdown": (currentNode) => (
      <AnswerDropdown
        choices={currentNode.choices_dropdown}
        FunctionOnClick={handleChoice}
      />
    ),
    "choices_likert": (currentNode) => (
      <AnswerLikert
        choices={currentNode.choices_likert}
        likertType={currentNode.likert_type}
        FunctionOnClick={handleChoice}
      />
    ),
    "choices_next_chapter": (currentNode) => (
      <AnswerUniqueHandle 
        choices={currentNode.choices_next_chapter}
        FunctionOnClickEnd={handleChapterComplete}
        FunctionOnClickContinue={handleChoice}
      />
    )
  };

  // dynamic change of repsonse
  const choiceKey = currentNode
    ? Object.keys(currentNode).find((key) => key.startsWith("choices") && renderAnswers[key])
    : null;

  // confirmations
  // const itemId = "confirmationAbort";
  // const confirmationAbort = chapters.find(chapter => chapter.id === itemId);

  return (
    <div 
      className={`${styles.background} ${theme[themeName]}`}
      style={{background: `url(${ImgBuilder[`teenBg_${themeName}`]}) center/cover no-repeat`}}
    >

      {isConfirmation && (
        <ConfirmationTeenGame 
          message={t("pop_up_notifications.message_social")}
          choices={t("pop_up_notifications.answers_social", { returnObjects: true })}
          variables={userData} 
          abortFunction={toggleConfirmationComponent}
        />
      )}

      {isSettings &&
        <SettingsTeenGame abortFunction={settingsFunction}/>
      }
      
      {isAnimating && (
        <AnimationChapterSbgame 
          setIsAnimating={setIsAnimating}
          variables={userData}
          setVariables={setUserData}
        />
      )}
      <div className={styles.chatContainer}>
        <div className={styles.chatWrapper}>
          {history.map((msg, idx) =>
            (renderHistory[msg.speaker] || renderHistory.default)(msg, idx)
          )}

          {isWaiting && <Loading />}

          

          {!isWaiting && choiceKey && renderAnswers[choiceKey](currentNode)}

          {/* {!isWaiting  && !isAnimating &&(
            <AnswerCommon 
              choices={[{ text: "Next Chapter" }]}
              FunctionOnClick={handleChapterComplete}
            />
          )} */}

          {/* // should not be a case */}
          {!isWaiting && !choiceKey && !isAnimating && (
            <AnswerCommon 
              choices={[{ text: "Next Chapter" }]}
              FunctionOnClick={handleChapterComplete}
            />
          )}

          <div className={styles.endRef} ref={chatEndRef} />
        </div>
      </div>
    </div>
  );
};

export default FactCheckerPage;
